/**
 * Data asset entity adapter (0183).
 *
 * The sixth dependency layer, finally a register rather than a word
 * on a service. Reuses the P15 scaffolding; the two things that make
 * it worth having -- where the data lives and which services touch it
 * -- are link sections on the drawer, because they are the answers a
 * single row cannot carry.
 *
 * Both vocabularies are CLOSED on purpose. `Service.data_classification`
 * is a free String and reads `confidential` on every service on the
 * estate, which is why the product could not aggregate anything about
 * data; a select rather than a text box is most of the fix.
 */
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export type DataClassification = "public" | "internal" | "confidential" | "secret";
export type DataCategory =
  | "customer_personal"
  | "payment"
  | "account"
  | "transaction"
  | "employee"
  | "operational"
  | "regulatory";

const CLASSIFICATION_LABELS: Record<DataClassification, string> = {
  public: "Public",
  internal: "Internal",
  confidential: "Confidential",
  secret: "Secret",
};

const CATEGORY_LABELS: Record<DataCategory, string> = {
  customer_personal: "Customer personal",
  payment: "Payment",
  account: "Account",
  transaction: "Transaction",
  employee: "Employee",
  operational: "Operational",
  regulatory: "Regulatory",
};

/** Sensitivity is not status, so this is a neutral ramp rather than
 *  the RAG palette -- secret data is not a red flag, it is data. */
const CLASSIFICATION_CLASS: Record<DataClassification, string> = {
  public: "bg-slate-100 text-slate-700",
  internal: "bg-slate-200 text-slate-800",
  confidential: "bg-indigo-100 text-indigo-900",
  secret: "bg-indigo-200 text-indigo-950",
};

export interface DataAsset {
  id: string;
  code: string;
  name: string;
  description: string | null;
  classification: DataClassification;
  category: DataCategory;
  owner: string | null;
  created_at: string;
  updated_at: string;
}

export interface DataAssetIn {
  code: string;
  name: string;
  description: string | null;
  classification: DataClassification;
  category: DataCategory;
  owner: string | null;
}

export type DataAssetPatch = Partial<Omit<DataAssetIn, "code">>;

const columns: AdminColumn<DataAsset>[] = [
  {
    key: "name",
    label: "Data asset",
    sortValue: (r) => r.name,
    /* Round 11 F3 -- `ms-2` separates the name from the code on screen
       and not in the text, so the register read "Account balances and
       ledgerda.demo.account_ledger" to anything that takes innerText:
       a copy into a board pack, a screen reader, a CSV pasted out of
       the table. A margin is not a separator. */
    render: (r) => (
      <span>
        <span className="font-medium text-slate-900">{r.name}</span>
        <span className="ms-2 font-mono text-xs text-slate-500">
          <span aria-hidden="true"> · </span>
          {r.code}
        </span>
      </span>
    ),
  },
  {
    key: "classification",
    label: "Classification",
    sortValue: (r) => r.classification,
    render: (r) => (
      <span
        data-testid={`data-asset-classification-${r.classification}`}
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CLASSIFICATION_CLASS[r.classification]}`}
      >
        {CLASSIFICATION_LABELS[r.classification]}
      </span>
    ),
  },
  {
    key: "category",
    label: "Category",
    sortValue: (r) => r.category,
    render: (r) => (
      <span className="text-xs text-slate-700">{CATEGORY_LABELS[r.category]}</span>
    ),
  },
  {
    key: "owner",
    label: "Owner",
    render: (r) =>
      r.owner ? (
        <span className="text-xs text-slate-700">{r.owner}</span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
];

const formFields: FormField<DataAssetIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 64 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  {
    kind: "select",
    key: "classification",
    label: "Classification",
    required: true,
    options: (Object.keys(CLASSIFICATION_LABELS) as DataClassification[]).map((v) => ({
      value: v,
      label: CLASSIFICATION_LABELS[v],
    })),
  },
  {
    kind: "select",
    key: "category",
    label: "Category",
    required: true,
    options: (Object.keys(CATEGORY_LABELS) as DataCategory[]).map((v) => ({
      value: v,
      label: CATEGORY_LABELS[v],
    })),
  },
  { kind: "text", key: "owner", label: "Owner", maxLength: 255 },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
];

export const dataAssetAdapter: EntityAdapter<DataAsset, DataAssetIn, DataAssetPatch> = {
  i18nPrefix: "modules.foundation.admin.dataAssets",
  importKind: "data_asset",
  endpoint: "/v1/admin/foundation/data-assets",
  queryKey: ["foundation-admin", "data-assets"] as const,
  columns,
  defaultSort: { key: "name", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) =>
    `${r.code} ${r.name} ${r.classification} ${r.category} ${r.owner ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    description: null,
    classification: "internal",
    category: "operational",
    owner: null,
  }),
  rowToPatch: (r) => ({
    name: r.name,
    description: r.description,
    classification: r.classification,
    category: r.category,
    owner: r.owner,
  }),
};
